import { useState, useCallback, useRef } from "react";

/**
 * How long login() keeps waiting for the WA_EMBEDDED_SIGNUP message after the
 * FB.login callback. The code expires 30 seconds after it is issued.
 */
export const SESSION_INFO_WAIT_MS = 3000;

/**
 * Hook to lazy-load Facebook SDK on demand for WhatsApp Business Login
 * @returns {Object} SDK state and methods
 * - isLoaded: boolean - SDK loaded and initialized
 * - isLoading: boolean - SDK currently loading
 * - error: Error|null - Loading error if any
 * - loadSDK(appId): returns a Promise that resolves with the initialized FB SDK
 * - login(options): returns a Promise that wraps FB.login
 */
export const useFacebookSDK = () => {
  const [state, setState] = useState({
    isLoaded: false,
    isLoading: false,
    error: null,
  });
  const loadPromiseRef = useRef(null);

  const loadSDK = useCallback((appId) => {
    // If already loaded, return immediately
    if (window.FB) {
      setState({ isLoaded: true, isLoading: false, error: null });
      return Promise.resolve(window.FB);
    }

    // If already loading, return existing promise
    if (loadPromiseRef.current) {
      return loadPromiseRef.current;
    }

    setState((s) => ({ ...s, isLoading: true, error: null }));

    loadPromiseRef.current = new Promise((resolve, reject) => {
      // Setup callback before script loads
      window.fbAsyncInit = () => {
        window.FB.init({
          appId,
          cookie: true,
          xfbml: true,
          version: "v25.0",
        });
        setState({ isLoaded: true, isLoading: false, error: null });
        resolve(window.FB);
      };

      // Check if script already exists
      const existingScript = document.querySelector(
        'script[src*="connect.facebook.net"]',
      );
      if (existingScript) {
        // Script exists but FB not ready, wait for fbAsyncInit
        return;
      }

      // Create and load script
      const script = document.createElement("script");
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";

      script.onerror = () => {
        const error = new Error("Failed to load Facebook SDK");
        setState({ isLoaded: false, isLoading: false, error });
        loadPromiseRef.current = null;
        reject(error);
      };

      document.body.appendChild(script);
    });

    return loadPromiseRef.current;
  }, []);

  const login = useCallback((options) => {
    return new Promise((resolve, reject) => {
      if (!window.FB) {
        reject(new Error("Facebook SDK not loaded"));
        return;
      }

      // Capture session info from the WA_EMBEDDED_SIGNUP message event.
      // Meta does not order it with the FB.login callback, so it is still
      // awaited for a while after the callback.
      let sessionInfo = null;
      let loginResponse = null;
      let waitTimer = null;
      const finish = () => {
        window.removeEventListener("message", handleMessage);
        clearTimeout(waitTimer);
        resolve({ ...loginResponse, sessionInfo });
      };
      const handleMessage = (event) => {
        if (!event.origin.endsWith("facebook.com")) return;
        try {
          const data =
            typeof event.data === "string"
              ? JSON.parse(event.data)
              : event.data;
          if (data?.type === "WA_EMBEDDED_SIGNUP") {
            sessionInfo = data;
            if (loginResponse) finish();
          }
        } catch {
          // Non-JSON message, ignore
        }
      };
      window.addEventListener("message", handleMessage);

      window.FB.login((response) => {
        if (!response.authResponse) {
          window.removeEventListener("message", handleMessage);
          reject(new Error("Login cancelled or failed"));
          return;
        }
        loginResponse = response;
        if (sessionInfo) {
          finish();
        } else {
          waitTimer = setTimeout(finish, SESSION_INFO_WAIT_MS);
        }
      }, options);
    });
  }, []);

  return {
    ...state,
    loadSDK,
    login,
    FB: window.FB,
  };
};

export default useFacebookSDK;
